import api from '../libs/axiosInstance';
import firebase from 'firebase/app';
import 'firebase/auth';
import { cacheService } from './cache.service';
import { captureException } from '@sentry/react';

export interface Goal {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  status: 'not_started' | 'in_progress' | 'completed' | 'paused';
  progress: number;
  dueDate: string;
  createdAt: string;
  updatedAt: string;
  milestones: Milestone[];
  tags: string[];
  estimatedHours: number;
  actualHours: number;
  difficulty: 'easy' | 'medium' | 'hard';
  impact: 'low' | 'medium' | 'high';
  dependencies: string[];
  resources: Resource[];
  notes: string;
  attachments: Attachment[];
  isFavorite: boolean;
  isArchived: boolean;
  completionDate?: string;
  lastActivity: string;
  // New fields for session integration
  relatedSessions: string[];
  targetSessions?: number;
  completedSessions: number;
  averageSessionScore?: number;
  lastSessionDate?: string;
  sessionProgress: SessionProgress[];
  skillsToImprove: string[];
  targetScore?: number;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed';
  dueDate: string;
  completedDate?: string;
  progress: number;
}

export interface Resource {
  id: string;
  title: string;
  type: 'article' | 'video' | 'course' | 'book' | 'tool' | 'other';
  url: string;
  description: string;
  isCompleted: boolean;
}

export interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedAt: string;
}

export interface SessionProgress {
  sessionId: string;
  sessionTitle: string;
  sessionType: string;
  completedAt: string;
  score: number;
  contribution: number;
  skills: string[];
}

export interface GoalSessionLink {
  goalId: string;
  sessionId: string;
  contribution: number;
  skills: string[];
  linkedAt: string;
}

export interface GoalQuery {
  search?: string;
  status?: string[];
  priority?: string[];
  category?: string[];
  difficulty?: string[];
  impact?: string[];
  showArchived?: boolean;
  showFavorites?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreateGoalData {
  title: string;
  description: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  difficulty: 'easy' | 'medium' | 'hard';
  impact: 'low' | 'medium' | 'high';
  dueDate: string;
  estimatedHours: number;
  tags: string[];
  notes?: string;
  milestones?: Milestone[];
  resources?: Resource[];
  attachments?: Attachment[];
  dependencies?: string[];
  // New fields for session integration
  targetSessions?: number;
  skillsToImprove?: string[];
  targetScore?: number;
}

export interface UpdateGoalData {
  title?: string;
  description?: string;
  category?: string;
  priority?: 'high' | 'medium' | 'low';
  difficulty?: 'easy' | 'medium' | 'hard';
  impact?: 'low' | 'medium' | 'high';
  dueDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  progress?: number;
  status?: 'not_started' | 'in_progress' | 'completed' | 'paused';
  tags?: string[];
  notes?: string;
  milestones?: Milestone[];
  resources?: Resource[];
  attachments?: Attachment[];
  dependencies?: string[];
  isFavorite?: boolean;
  isArchived?: boolean;
  completionDate?: string;
}

export interface GoalsResponse {
  goals: Goal[];
  total: number;
  hasMore: boolean;
}

export interface AnalyticsData {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  paused: number;
  overdue: number;
  avgProgress: number;
  totalHours: number;
  estimatedHours: number;
  completionRate: number;
  categoryStats: Record<string, number>;
  priorityStats: Record<string, number>;
  difficultyStats: Record<string, number>;
  monthlyTrend?: Array<{
    month: string;
    completed: number;
    created: number;
  }>;
  categoryPerformance?: Array<{
    category: string;
    total: number;
    completed: number;
    avgProgress: number;
    completionRate: number;
  }>;
}

export interface Notification {
  id: string;
  type: 'deadline' | 'milestone' | 'overdue' | 'achievement' | 'reminder';
  title: string;
  message: string;
  goalId: string;
  goalTitle: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
  isRead?: boolean;
}

class GoalsService {
  private baseUrl = '/goals';

  /**
   * Invalidate all goals cache for a user
   */
  private invalidateUserGoalsCache(userId: string): void {
    const stats = cacheService.getStats();
    stats.keys.forEach(key => {
      if (key.includes(`user:${userId}:goals`)) {
        cacheService.delete(key);
      }
    });
  }

  async getGoals(query: GoalQuery = {}): Promise<GoalsResponse> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Generate cache key
      const cacheKey = cacheService.generateUserKey(user.uid, 'goals', query);
      
      // Check cache first
      const cachedData = cacheService.get<GoalsResponse>(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const params = new URLSearchParams();
      
      if (query.search) params.append('search', query.search);
      if (query.status?.length) {
        query.status.forEach(status => params.append('status', status));
      }
      if (query.priority?.length) {
        query.priority.forEach(priority => params.append('priority', priority));
      }
      if (query.category?.length) {
        query.category.forEach(category => params.append('category', category));
      }
      if (query.difficulty?.length) {
        query.difficulty.forEach(difficulty => params.append('difficulty', difficulty));
      }
      if (query.impact?.length) {
        query.impact.forEach(impact => params.append('impact', impact));
      }
      if (query.showArchived !== undefined) params.append('showArchived', query.showArchived.toString());
      if (query.showFavorites !== undefined) params.append('showFavorites', query.showFavorites.toString());
      if (query.sortBy) params.append('sortBy', query.sortBy);
      if (query.sortOrder) params.append('sortOrder', query.sortOrder);
      if (query.limit) params.append('limit', query.limit.toString());
      if (query.offset) params.append('offset', query.offset.toString());

      const url = `${this.baseUrl}/${user.uid}?${params.toString()}`;
      
      const response = await api.get(url);
      
      // Cache the result for 2 minutes
      cacheService.set(cacheKey, response.data, 2 * 60 * 1000);
      
      return response.data;
    } catch (error: any) {
      captureException(error);
      throw new Error('Failed to fetch goals');
    }
  }

  async getGoal(goalId: string): Promise<Goal> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`${this.baseUrl}/${user.uid}/${goalId}`);
      return response.data;
    } catch (error) {
      captureException(error);
      throw new Error('Failed to fetch goal');
    }
  }

  async createGoal(goalData: CreateGoalData): Promise<Goal> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`${this.baseUrl}/${user.uid}`, goalData);
      
      // Invalidate user's goals cache
      this.invalidateUserGoalsCache(user.uid);
      
      return response.data;
    } catch (error) {
      captureException(error);
      throw new Error('Failed to create goal');
    }
  }

  async updateGoal(goalId: string, goalData: UpdateGoalData): Promise<Goal> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}`, goalData);
      
      // Invalidate user's goals cache
      this.invalidateUserGoalsCache(user.uid);
      
      return response.data;
    } catch (error) {
      captureException(error);
      throw new Error('Failed to update goal');
    }
  }

  async deleteGoal(goalId: string): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      await api.delete(`${this.baseUrl}/${user.uid}/${goalId}`);
      
      // Invalidate user's goals cache
      this.invalidateUserGoalsCache(user.uid);
    } catch (error) {
      captureException(error);
      throw new Error('Failed to delete goal');
    }
  }

  async addMilestone(goalId: string, milestone: Omit<Milestone, 'id'>): Promise<Milestone> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`${this.baseUrl}/${user.uid}/${goalId}/milestones`, milestone);
      return response.data;
    } catch (error) {
      captureException(error);
      throw new Error('Failed to add milestone');
    }
  }

  async updateMilestone(goalId: string, milestoneId: string, milestone: Partial<Milestone>): Promise<Milestone> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}/milestones/${milestoneId}`, milestone);
      return response.data;
    } catch (error) {
      captureException(error);
      throw new Error('Failed to update milestone');
    }
  }

  async deleteMilestone(goalId: string, milestoneId: string): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      await api.delete(`${this.baseUrl}/${user.uid}/${goalId}/milestones/${milestoneId}`);
    } catch (error) {
      console.error('Error deleting milestone:', error);
      throw new Error('Failed to delete milestone');
    }
  }

  async addResource(goalId: string, resource: Omit<Resource, 'id'>): Promise<Resource> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`${this.baseUrl}/${user.uid}/${goalId}/resources`, resource);
      return response.data;
    } catch (error) {
      console.error('Error adding resource:', error);
      throw new Error('Failed to add resource');
    }
  }

  async updateResource(goalId: string, resourceId: string, resource: Partial<Resource>): Promise<Resource> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}/resources/${resourceId}`, resource);
      return response.data;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw new Error('Failed to update resource');
    }
  }

  async deleteResource(goalId: string, resourceId: string): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      await api.delete(`${this.baseUrl}/${user.uid}/${goalId}/resources/${resourceId}`);
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw new Error('Failed to delete resource');
    }
  }

  async updateProgress(goalId: string, progress: number, actualHours?: number): Promise<Goal> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}/progress`, {
        progress,
        actualHours
      });
      return response.data;
    } catch (error) {
      console.error('Error updating progress:', error);
      throw new Error('Failed to update progress');
    }
  }

  async updateStatus(goalId: string, status: string, completionDate?: string): Promise<Goal> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}/status`, {
        status,
        completionDate
      });
      return response.data;
    } catch (error) {
      console.error('Error updating status:', error);
      throw new Error('Failed to update status');
    }
  }

  async getAnalytics(): Promise<AnalyticsData> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`${this.baseUrl}/${user.uid}/analytics/overview`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw new Error('Failed to fetch analytics');
    }
  }

  async getNotifications(): Promise<Notification[]> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`${this.baseUrl}/${user.uid}/notifications`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw new Error('Failed to fetch notifications');
    }
  }

  // Session integration methods
  async linkSessionToGoal(goalId: string, sessionId: string, contribution: number, skills: string[]): Promise<GoalSessionLink> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`${this.baseUrl}/${user.uid}/${goalId}/sessions/${sessionId}`, {
        contribution,
        skills
      });
      return response.data;
    } catch (error) {
      console.error('Error linking session to goal:', error);
      throw new Error('Failed to link session to goal');
    }
  }

  async unlinkSessionFromGoal(goalId: string, sessionId: string): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      await api.delete(`${this.baseUrl}/${user.uid}/${goalId}/sessions/${sessionId}`);
    } catch (error) {
      console.error('Error unlinking session from goal:', error);
      throw new Error('Failed to unlink session from goal');
    }
  }

  async getGoalSessions(goalId: string): Promise<SessionProgress[]> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`${this.baseUrl}/${user.uid}/${goalId}/sessions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching goal sessions:', error);
      throw new Error('Failed to fetch goal sessions');
    }
  }

  async calculateProgressFromSessions(goalId: string): Promise<Goal> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}/progress/calculate`);
      return response.data;
    } catch (error) {
      console.error('Error calculating progress from sessions:', error);
      throw new Error('Failed to calculate progress from sessions');
    }
  }

  async getGoalsForSession(sessionId: string): Promise<Goal[]> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`${this.baseUrl}/${user.uid}/session/${sessionId}/goals`);
      return response.data;
    } catch (error) {
      console.error('Error fetching goals for session:', error);
      throw new Error('Failed to fetch goals for session');
    }
  }

  async updateSessionContribution(goalId: string, sessionId: string, contribution: number, skills: string[]): Promise<GoalSessionLink> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`${this.baseUrl}/${user.uid}/${goalId}/sessions/${sessionId}`, {
        contribution,
        skills
      });
      return response.data;
    } catch (error) {
      console.error('Error updating session contribution:', error);
      throw new Error('Failed to update session contribution');
    }
  }

  // Email notification methods
  async getEmailPreferences(): Promise<any> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`${this.baseUrl}/${user.uid}/email-preferences`);
      return response.data;
    } catch (error: any) {
      captureException(error);
      throw new Error('Failed to get email preferences');
    }
  }

  async updateEmailPreferences(preferences: any): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      await api.put(`${this.baseUrl}/${user.uid}/email-preferences`, preferences);
    } catch (error: any) {
      captureException(error);
      throw new Error('Failed to update email preferences');
    }
  }

  async scheduleNotifications(): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      await api.post(`${this.baseUrl}/${user.uid}/schedule-notifications`);
    } catch (error) {
      captureException(error);
      throw new Error('Failed to schedule notifications');
    }
  }
}

export const goalsService = new GoalsService();
